<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import mapboxgl, { LngLatBoundsLike } from "mapbox-gl";
import MapboxglSpiderifier from "mapboxgl-spiderifier";
import CmsDataProvider, { CMS_DATA_ID } from "@/providers/cms.provider";
import InteractieveKaartOverlay from "@/views/InteractieveKaart/components/InteractieveKaartOverlay.vue";
import InteractieveKaartSidebar from "@/views/InteractieveKaart/components/InteractieveKaartSidebar.vue";
import InteractieveKaartActions from "@/views/InteractieveKaart/components/InteractieveKaartActions.vue";
import type { ActivityType } from "@/views/InteractieveKaart/components/InteractieveKaartSidebar.vue";
import activiteitRepository, { Activiteit, ActiviteitItem, IActiviteitSearchParams } from "@/repositories/activiteit.repository";
import sportaanbiederRepository, { ISportaanbieder, ISportaanbiederSearchParams, SportaanbiederEntity } from "@/repositories/sportaanbieder.repository";
import { usePlatformStore } from "@/stores/platform.store";
import { Notifications, notify } from "@kyvg/vue3-notification";
import interactieveKaartRepository, { IInteractieveKaartPinDetail, type IInteractieveKaartPin } from "@/repositories/interactieve-kaart.repository";
import type { FacetGroup } from "@/models/search.model";
import { generateFacetList } from "@/helpers/facet.helper";
import type { HiddenFilters } from "@/view-models/filters.model";
import { getMapboxAccessToken } from "@/helpers/config.helper";

type PinType = "aanbieder" | "activiteit" | "speelplek" | "wandelroute" | "fietsroute";
type RoutePinType = Extract<PinType, "wandelroute" | "fietsroute">;
type ActivitySidebarFilters = Record<string, string[] | number>;

const cmsData = CmsDataProvider.get<{
	id: string,
	activitiesSingular: string,
	activitiesPlural: string,
	activitiesDetailPageUrl: string,
	activitiesBestelpaginaUrl: string,
	clubsSingular: string,
	clubsPlural: string,
	clubsDetailPageUrl: string,
	mapCentre: { latitude: number | string, longitude: number | string },
	mapMinBound: { latitude: number | string, longitude: number | string },
	mapMaxBound: { latitude: number | string, longitude: number | string },
	mapZoom: number,
	pins?: MapInteractieveKaartPin[];
	hiddenFiltersActivities?: HiddenFilters;
	hiddenFiltersClubs?: HiddenFilters;
}>(CMS_DATA_ID.INTERACTIEVE_KAART);

interface MapInteractieveKaartPin {
	id: number;
	guid?: string;
	title: string;
	type: PinType;
	latitude: number;
	longitude: number;
	kind: "activity" | "play";
	aanbiederId?: string;
	gpxXml?: string;
	gpxUrl?: string;
	sportmomentId?: number;
	address?: string;
	facets?: Record<string, string[]>;
	description?: string;
	isLoadingDetails?: boolean;
	imageUrl?: string;
	infoUrl?: string;
	bookingUrl?: string;
	externalBookingUrl?: string;
	waitlistUrl?: string;
	bookingType?: number;
	availableSpots?: number;
	isSamePlatform?: boolean;
	showBookButton?: boolean;
	showWaitlist?: boolean;
	displayLabel?: string;
	routeDistance?: number | null;
	routeInspanning?: string[];
	routeTerreintype?: string[];
}

interface MapFeatureProperties {
	[key: string]: unknown;
	id: number;
	title: string;
	pinType: PinType;
	latitude: number;
	longitude: number;
}

type SpiderifierLeaf = MapFeatureProperties;

interface RouteFeatureProperties {
	[key: string]: unknown;
	pinId: number;
	routeColor: string;
}

const SOURCE_ID = "interactieve-kaart-pins";
const ROUTE_SOURCE_ID = "interactieve-kaart-route";
const ROUTE_BASE_LAYER_ID = "interactieve-kaart-route-base";
const ROUTE_COLOR_LAYER_ID = "interactieve-kaart-route-color";
const CLUSTER_SHADOW_LAYER_ID = "interactieve-kaart-clusters-shadow";
const CLUSTER_LAYER_ID = "interactieve-kaart-clusters";
const CLUSTER_COUNT_LAYER_ID = "interactieve-kaart-cluster-count";
const UNCLUSTERED_LAYER_ID = "interactieve-kaart-pin";
const SPIDERFY_FROM_ZOOM = 12;
const ROUTE_NEUTRAL_COLOR = "#44515B"; // matches $gray-700
const ACTIVE_PIN_CLASS = "is-active";

const platformStore = usePlatformStore();

const mapElement = ref<HTMLDivElement | null>(null);
const mapInstance = ref<mapboxgl.Map | null>(null);
const selectedPin = ref<MapInteractieveKaartPin | null>(null);
const selectedActivity = ref<ActivityType>("bewegen");
const selectedPlayTypes = ref<string[]>([]);
const activityFilters = ref<ActivitySidebarFilters>({});
const clubFilters = ref<Record<string, string[]>>({});
const activityFacetGroups = ref<FacetGroup[]>([]);
const clubFacetGroups = ref<FacetGroup[]>([]);
const activityPins = ref<MapInteractieveKaartPin[]>([]);
const playPins = ref<MapInteractieveKaartPin[]>([]);
const clubPins = ref<MapInteractieveKaartPin[]>([]);

let spiderifier: MapboxglSpiderifier | null = null;
let activeClusterId: number | null = null;
let previousBodyOverflow = "";
let unclusteredMarkers: mapboxgl.Marker[] = [];
let userLocationMarker: mapboxgl.Marker | null = null;
let hoveredRoutePinId: number | null = null;
let activeRoutePinId: number | null = null;
const routeDataCache = new Map<number, GeoJSON.FeatureCollection<GeoJSON.LineString, RouteFeatureProperties>>();
const loadedPinDetailKeys = new Set<string>();
const loadingPinDetailKeys = new Set<string>();

const pinColors: Record<PinType, string> = {
	aanbieder: "#F2A9FD",
	activiteit: "#ffaaaa",
	fietsroute: "#4DE1FF",
	speelplek: "#3BE769",
	wandelroute: "#F4D850",
};


const pinIcons: Record<PinType, string> = {
	aanbieder: "icon-home",
	activiteit: "icon-agenda",
	fietsroute: "icon-bike",
	speelplek: "icon-playground",
	wandelroute: "icon-hiking",
};

const mapPlayPinToPin = (item: IInteractieveKaartPin, index: number): MapInteractieveKaartPin => ({
	id: 9000000 + index,
	guid: item.Id,
	title: item.Title,
	type: item.Type.toLowerCase() as PinType,
	kind: item.Kind as "activity" | "play",
	latitude: item.Latitude,
	longitude: item.Longitude,
	address: item.Address,
	gpxXml: item.GpxXml || undefined,
	gpxUrl: item.GpxUrl || undefined,
	displayLabel: item.DisplayLabel,
	facets: {},
	imageUrl: item.ImageUrl,
	infoUrl: item.InfoUrl
});

const emptyRouteGeoJson: GeoJSON.FeatureCollection<GeoJSON.LineString, RouteFeatureProperties> = {
	type: "FeatureCollection",
	features: [],
};

const pins = computed(() => {
	if (selectedActivity.value === "spelen") {
		return [...playPins.value, ...clubPins.value];
	}

	return activityPins.value;
});
const filteredPins = computed(() => {
	if (selectedActivity.value === "spelen") {
		const hasPlayTypeFilter = selectedPlayTypes.value.length > 0;
		const selectedTypes = new Set(selectedPlayTypes.value);
		const hasClubFilters = Object.values(clubFilters.value).some((values) => values.length > 0);

		return pins.value.filter((pin) => {
			if (hasClubFilters && pin.type !== "aanbieder") return false;
			if (hasPlayTypeFilter && !selectedTypes.has(pin.type)) return false;
			return true;
		});
	}

	return pins.value;
});


const overlayPins = computed<any[]>(() => filteredPins.value as any[]);

const parseNumber = (value: unknown): number | null => {
	if (typeof value === "number") {
		return Number.isFinite(value) ? value : null;
	}

	if (typeof value === "string") {
		const parsed = Number(value.replace(",", "."));
		return Number.isFinite(parsed) ? parsed : null;
	}

	return null;
}

const getCenterFromPins = (): [number, number] => {
	if (pins.value.length === 0) {
		return [5.1214, 52.0907];
	}

	const longitude = pins.value.reduce((sum, pin) => sum + pin.longitude, 0) / pins.value.length;
	const latitude = pins.value.reduce((sum, pin) => sum + pin.latitude, 0) / pins.value.length;

	return [longitude, latitude];
}

const getResolvedMapCenter = (): [number, number] => {
	const longitude = parseNumber(cmsData?.mapCentre?.longitude);
	const latitude = parseNumber(cmsData?.mapCentre?.latitude);

	const hasUsableCmsCenter = longitude !== null
		&& latitude !== null
		&& latitude >= -90
		&& latitude <= 90
		&& longitude >= -180
		&& longitude <= 180
		&& !(latitude === 0 && longitude === 0);

	if (hasUsableCmsCenter) {
		return [longitude, latitude];
	}

	return getCenterFromPins();
}

const getResolvedMapBounds = (): LngLatBoundsLike => {
	const fallbackBounds: [[number, number], [number, number]] = [
		[1.31, 49.8],
		[9.09, 54.51],
	];

	const minLng = parseNumber(cmsData?.mapMinBound?.longitude);
	const minLat = parseNumber(cmsData?.mapMinBound?.latitude);
	const maxLng = parseNumber(cmsData?.mapMaxBound?.longitude);
	const maxLat = parseNumber(cmsData?.mapMaxBound?.latitude);

	if (minLng === null || minLat === null || maxLng === null || maxLat === null) {
		return fallbackBounds;
	}

	if (Math.abs(maxLng - minLng) < 0.01 || Math.abs(maxLat - minLat) < 0.01) {
		return fallbackBounds;
	}

	return [
		[Math.min(minLng, maxLng), Math.min(minLat, maxLat)],
		[Math.max(minLng, maxLng), Math.max(minLat, maxLat)],
	];
}

const getResolvedInitialZoom = (): number => {
	const zoom = parseNumber(cmsData?.mapZoom);
	if (zoom === null) {
		return 7;
	}

	return Math.min(Math.max(zoom, 2), 15);
}

const mapCenter = getResolvedMapCenter();
const mapBounds = getResolvedMapBounds();
const initialZoom = getResolvedInitialZoom();
const minZoom = Math.max(1, initialZoom - 4);
const maxZoom = 17;

const geoJsonData = computed<GeoJSON.FeatureCollection<GeoJSON.Point, MapFeatureProperties>>(() => {
	return {
		type: "FeatureCollection",
		features: filteredPins.value.map((pin) => ({
			type: "Feature",
			geometry: {
				type: "Point",
				coordinates: [pin.longitude, pin.latitude],
			},
			properties: {
				id: pin.id,
				title: pin.title,
				pinType: pin.type,
				latitude: pin.latitude,
				longitude: pin.longitude,
			},
		})),
	};
});

const clearUnclusteredMarkers = (): void => {
	for (const marker of unclusteredMarkers) {
		marker.remove();
	}
	unclusteredMarkers = [];
};

const toInteractieveKaartPin = (properties: MapFeatureProperties): MapInteractieveKaartPin => {
	return {
		id: properties.id,
		title: properties.title,
		type: properties.pinType,
		kind: properties.pinType === "activiteit" ? "activity" : "play",
		latitude: properties.latitude,
		longitude: properties.longitude,
	};
};

const getClusterSource = (): (mapboxgl.GeoJSONSource & {
	getClusterLeaves: (
		clusterId: number,
		limit: number,
		offset: number,
		callback: (error: Error | null, features: GeoJSON.Feature[] | null) => void
	) => void;
}) | null => {
	const map = mapInstance.value;
	if (!map) {
		return null;
	}

	const source = map.getSource(SOURCE_ID);
	if (!source) {
		return null;
	}

	return source as mapboxgl.GeoJSONSource & {
		getClusterLeaves: (
			clusterId: number,
			limit: number,
			offset: number,
			callback: (error: Error | null, features: GeoJSON.Feature[] | null) => void
		) => void;
	};
};

const getClusterLeaves = async (clusterId: number): Promise<SpiderifierLeaf[]> => {
	const source = getClusterSource();
	if (!source) {
		return [];
	}

	return new Promise((resolve) => {
		source.getClusterLeaves(clusterId, 100, 0, (error, features) => {
			if (error || !features) {
				resolve([]);
				return;
			}

			resolve(
				features
					.map((feature) => toMapFeatureProperties(feature.properties))
					.filter((feature): feature is SpiderifierLeaf => feature !== null),
			);
		});
	});
};

const spiderfyCluster = async (clusterId: number, clusterCoordinates: [number, number]): Promise<void> => {
	if (!spiderifier) {
		return;
	}

	const leaves = await getClusterLeaves(clusterId);
	if (leaves.length === 0) {
		return;
	}

	unspider();
	spiderifier.spiderfy([clusterCoordinates[0], clusterCoordinates[1]], leaves);
	activeClusterId = clusterId;
	requestAnimationFrame(() => {
		syncActivePinVisualState();
	});
};

const revealPinInSpiderifier = async (pinId: number): Promise<void> => {
	const map = mapInstance.value;
	if (!map || map.getZoom() < Math.min(maxZoom, SPIDERFY_FROM_ZOOM)) {
		return;
	}

	const clusterFeatures = map.querySourceFeatures(SOURCE_ID, {
		filter: ["has", "point_count"],
	});

	for (const clusterFeature of clusterFeatures) {
		const clusterId = Number(clusterFeature.properties?.cluster_id);
		if (!Number.isFinite(clusterId) || clusterFeature.geometry.type !== "Point") {
			continue;
		}

		const leaves = await getClusterLeaves(clusterId);
		if (!leaves.some((leaf) => leaf.id === pinId)) {
			continue;
		}

		const clusterCoordinates = clusterFeature.geometry.coordinates as [number, number];
		await spiderfyCluster(clusterId, clusterCoordinates);
		return;
	}

	unspider();
};

const scheduleRevealPinInSpiderifier = (pinId: number): void => {
	const map = mapInstance.value;
	if (!map) {
		return;
	}

	const reveal = () => {
		void revealPinInSpiderifier(pinId);
	};

	if (map.isMoving()) {
		map.once("idle", reveal);
		return;
	}

	requestAnimationFrame(reveal);
};

const focusPinOnMap = (pin: MapInteractieveKaartPin, options?: { zoom?: number; revealInSpiderifier?: boolean }): void => {
	const map = mapInstance.value;
	if (!map) {
		return;
	}

	const targetZoom = options?.zoom !== undefined
		? Math.max(map.getZoom(), options.zoom)
		: map.getZoom();

	// Shift the target point upward so it doesn't hide behind the bottom overlay (~132px)
	// and rightward on large screens to account for the 358px filter panel.
	const overlayOffsetY = -80;
	const filterOffsetX = window.innerWidth >= 992 ? 179 : 0;

	map.easeTo({
		center: [pin.longitude, pin.latitude],
		zoom: targetZoom,
		offset: [filterOffsetX, overlayOffsetY],
		duration: 500,
	});

	if (options?.revealInSpiderifier) {
		scheduleRevealPinInSpiderifier(pin.id);
	}
};

const selectPin = (pin: MapInteractieveKaartPin, options?: { centerMap?: boolean; zoom?: number; revealInSpiderifier?: boolean }): void => {
	const resolvedPin = filteredPins.value.find((item) => item.id === pin.id) ?? pin;

	setActiveRoutePinId(resolvedPin.id, resolvedPin.type);
	selectedPin.value = resolvedPin;
	void ensurePinDetails(resolvedPin);

	if (options?.centerMap !== false) {
		focusPinOnMap(resolvedPin, { zoom: options?.zoom, revealInSpiderifier: options?.revealInSpiderifier });
		return;
	}

	if (options?.revealInSpiderifier) {
		scheduleRevealPinInSpiderifier(resolvedPin.id);
	}
};

const clearSelectedPin = (): void => {
	activeRoutePinId = null;
	selectedPin.value = null;
	void syncDisplayedRoute();
};

const getPinDetailKey = (pin: MapInteractieveKaartPin): string | null => {
	if (pin.kind === "activity") {
		const sportmomentId = pin.sportmomentId ?? pin.id;
		return Number.isFinite(sportmomentId) ? `activity:${sportmomentId}` : null;
	}

	if (pin.type === "aanbieder") {
		return pin.aanbiederId ? `provider:${pin.aanbiederId}` : null;
	}

	if(pin.kind === 'play') {
		return pin.id ? `play:${pin.id}` : null
	}

	return null;
};

const resolveActivityDescription = (detail: ActiviteitItem): string => {
	return detail.Beschrijving || detail.ExtraInformatie || "";
};

const resolveProviderDescription = (detail: ISportaanbieder): string => {
	return detail.Informatie || detail.Introductie || "";
};

const resolvePlayDescription = (detail: IInteractieveKaartPinDetail): string => {
	return detail.Description || "";
};

const ensurePinDetails = async (pin: MapInteractieveKaartPin): Promise<void> => {
	const key = getPinDetailKey(pin);
	if (!key || loadedPinDetailKeys.has(key) || loadingPinDetailKeys.has(key)) {
		return;
	}

	loadingPinDetailKeys.add(key);
	pin.isLoadingDetails = true;
	try {
		if (pin.kind === "activity") {
			const sportmomentId = pin.sportmomentId ?? pin.id;
			const detail = await activiteitRepository.getById(sportmomentId, platformStore.platformId);
			pin.description = resolveActivityDescription(detail);
		} else if (pin.type === "aanbieder" && pin.aanbiederId) {
			const detail = await sportaanbiederRepository.getSportsProviderById(pin.aanbiederId);
			pin.description = resolveProviderDescription(detail);
		}else if (pin.kind === 'play') {
			const detail = await interactieveKaartRepository.getPlayPinById(cmsData.id, pin.guid!);
			pin.description = resolvePlayDescription(detail);
			pin.routeDistance = detail.RouteDistance;
			pin.routeInspanning = detail.RouteInspanning ?? [];
			pin.routeTerreintype = detail.RouteTerreintype ?? [];
		}
		loadedPinDetailKeys.add(key);
	} catch (error) {
		notify({
			type: "error",
			title: "Fout bij laden van detailinformatie",
			duration: 4000,
		});
	} finally {
		pin.isLoadingDetails = false;
		loadingPinDetailKeys.delete(key);
	}
};


const getAvailableSpots = (entity: Activiteit): number => {
	if (entity.AantalPlaatsen <= entity.AantalInschrijvingen) {
		return 0;
	}

	if (entity.TotaalAantalPlaatsen !== entity.AantalPlaatsen) {
		const availablePlaces = entity.TotaalAantalPlaatsen - entity.AantalPlaatsen;
		const availablePlacesPlatform = entity.AantalPlaatsen - entity.AantalInschrijvingen;

		if (availablePlaces > entity.AantalPlaatsen || availablePlaces > availablePlacesPlatform) {
			return availablePlacesPlatform;
		}

		return availablePlaces;
	}

	return entity.TotaalAantalPlaatsen - entity.TotaalAantalInschrijvingen;
};

const mapActivityToPin = (entity: Activiteit): MapInteractieveKaartPin => {
	const availableSpots = getAvailableSpots(entity);
	const activityUrl = `${cmsData.activitiesDetailPageUrl}?id=${entity.SportmomentId}`;
	const bookingUrl = `${cmsData.activitiesBestelpaginaUrl}?id=${entity.SportmomentId}`;

	return {
		id: entity.SportmomentId,
		title: entity.Naam,
		type: "activiteit",
		kind: "activity",
		latitude: entity.LocatieItem.Latitude,
		longitude: entity.LocatieItem.Longitude,
		sportmomentId: entity.SportmomentId,
		address: [entity.LocatieItem.Naam, entity.LocatieItem.Adres || entity.LocatieItem.Woonplaats].filter(Boolean).join(", "),
		imageUrl: entity.AfbeeldingUrl,
		infoUrl: activityUrl,
		bookingUrl,
		availableSpots,
		bookingType: entity.Boekingstype,
		isSamePlatform: entity.PlatformItem.Id === platformStore.platformId,
		showBookButton: entity.Boekingstype === 1 && availableSpots >= 1,
		showWaitlist: entity.Boekingstype === 1 && availableSpots < 1,
		displayLabel: "Activiteit",
	};
};

const mapClubToPin = (entity: SportaanbiederEntity): MapInteractieveKaartPin => {
	const clubUrl = `${cmsData.clubsDetailPageUrl}?id=${entity.SportaanbiederId}`;

	return {
		id: Math.random() * 10000000,
		title: entity.Naam,
		type: "aanbieder",
		kind: "play",
		aanbiederId: entity.SportaanbiederId,
		latitude: entity.LocatieItem?.Latitude ?? 0,
		longitude: entity.LocatieItem?.Longitude ?? 0,
		address: [entity.LocatieItem?.Naam, entity.LocatieItem?.Adres || entity.LocatieItem?.Woonplaats]
			.filter(Boolean)
			.join(", "),
		imageUrl: entity.AfbeeldingUrl,
		infoUrl: clubUrl,
		displayLabel: "Aanbieder",
	};
};

const filterEmptyValues = <T extends Record<string, string[]>>(filters: T): Partial<T> => {
	const filteredEntries = Object.entries(filters).filter(([, values]) => Array.isArray(values) && values.length > 0);
	return Object.fromEntries(filteredEntries) as Partial<T>;
};

const filterActivityValues = (filters: ActivitySidebarFilters): Partial<IActiviteitSearchParams> => {
	const filteredEntries = Object.entries(filters).filter(([, value]) => {
		if (Array.isArray(value)) {
			return value.length > 0;
		}

		return typeof value === "number" && Number.isFinite(value);
	});

	return Object.fromEntries(filteredEntries) as Partial<IActiviteitSearchParams>;
};

const loadActivityPins = async (filters: ActivitySidebarFilters = {}): Promise<void> => {
	try {
		const activityParams: IActiviteitSearchParams = {
			type: "sportaanbod",
			platform_facet: platformStore.platformId,
			facetlist: generateFacetList(cmsData.hiddenFiltersActivities ?? {}, platformStore.isGolfPlatform)
				.filter((facet) => facet !== "activiteittypen_facet"),
			pageSize: 999999,
			offset: 0,
			OrderBy: "StartDatum",
			activiteittypen_facet: selectedActivity.value === "beleven" ? ["Cultuuractiviteit"] : ["Sportactiviteit"],
			...filterActivityValues(filters),
		};

		const response = await activiteitRepository.search(activityParams);
		activityPins.value = (response.Items ?? []).map((item) => mapActivityToPin(item.Entity));
		activityFacetGroups.value = response.Facets ?? [];
	} catch (error) {
		notify({
			type: "error",
			title: "Fout bij laden van activiteiten",
			duration: 5000,
		});
	}
};

const loadClubPins = async (filters: Record<string, string[]> = {}): Promise<void> => {
	try {
		const clubParams: ISportaanbiederSearchParams = {
			type: "sportaanbieder",
			platform_facet: platformStore.isGlobalPlatform ? undefined : platformStore.platformId,
			facetlist: generateFacetList(cmsData.hiddenFiltersClubs ?? {}, platformStore.isGolfPlatform)
				.filter((facet) => facet !== "activiteittypen_facet"),
			pageSize: 999999,
			offset: 0,
			OrderBy: "Naam",
			...filterEmptyValues(filters),
		};

		const response = await sportaanbiederRepository.search(clubParams);
		clubPins.value = (response.Items ?? []).map((item) => mapClubToPin(item.Entity));
		clubFacetGroups.value = response.Facets ?? [];
	} catch (error) {
		notify({
			type: "error",
			title: "Fout bij laden van aanbieders",
			duration: 5000,
		});
	}
};

const loadPlayPins = async (): Promise<void> => {
	try {
		const response = await interactieveKaartRepository.getPlayPins(cmsData.id);
		playPins.value = response.Items.map(mapPlayPinToPin);
		void loadClubPins(clubFilters.value);
	} catch (error) {
		notify({
			type: "error",
			title: "Fout bij laden van spelen",
			duration: 5000,
		});
	}
};

const onOverlaySelectPin = (pinId: number | null): void => {
	if (pinId === null) {
		clearSelectedPin();
		return;
	}

	const pin = filteredPins.value.find((item) => item.id === pinId);
	if (!pin) {
		return;
	}

	selectPin(pin, { centerMap: true, zoom: 15, revealInSpiderifier: true });
};

const onOverlayFocusPin = (pinId: number): void => {
	const pin = filteredPins.value.find((item) => item.id === pinId);
	if (!pin) {
		return;
	}

	focusPinOnMap(pin);
};

const onOverlayHoverPin = (pinId: number | null): void => {
	if (pinId === null) {
		hoveredRoutePinId = null;
		void syncDisplayedRoute();
		return;
	}

	const pin = filteredPins.value.find((item) => item.id === pinId);
	if (!pin || !isRouteType(pin.type)) {
		return;
	}

	hoveredRoutePinId = pinId;
	void syncDisplayedRoute();
};

const zoomMapBy = (delta: number): void => {
	const map = mapInstance.value;
	if (!map) {
		return;
	}

	const nextZoom = Math.min(Math.max(map.getZoom() + delta, minZoom), maxZoom);
	map.easeTo({ zoom: nextZoom, duration: 250 });
};

const onZoomIn = (): void => {
	zoomMapBy(1);
};

const onZoomOut = (): void => {
	zoomMapBy(-1);
};

const onLocateUser = (): void => {
	const map = mapInstance.value;
	if (!map || !navigator.geolocation) {
		return;
	}

	navigator.geolocation.getCurrentPosition((position) => {
		const longitude = position.coords.longitude;
		const latitude = position.coords.latitude;

		if (userLocationMarker) {
			userLocationMarker.remove();
		}

		userLocationMarker = new mapboxgl.Marker({ color: "#1D4ED8" })
			.setLngLat([longitude, latitude])
			.addTo(map as any);

		const filterOffsetX = window.innerWidth >= 992 ? 179 : 0;
		map.easeTo({
			center: [longitude, latitude],
			zoom: Math.max(map.getZoom(), 14),
			offset: [filterOffsetX, -80],
			duration: 500,
		});
	});
};

const renderUnclusteredMarkers = (): void => {
	const map = mapInstance.value;
	if (!map) {
		return;
	}

	clearUnclusteredMarkers();
	if (!map.getSource(SOURCE_ID)) {
		return;
	}

	const features = map.queryRenderedFeatures(undefined, {
		layers: [UNCLUSTERED_LAYER_ID],
		filter: ["!", ["has", "point_count"]],
	});

	const added = new Set<number>();

	for (const feature of features) {
		if (feature.geometry.type !== "Point") {
			continue;
		}

		const properties = toMapFeatureProperties(feature.properties);
		if (!properties || added.has(properties.id)) {
			continue;
		}

		added.add(properties.id);

		const markerElement = document.createElement("button");
		markerElement.type = "button";
		markerElement.className = "interactieve-kaart__pin-marker";
		if (selectedPin.value?.id === properties.id) {
			markerElement.classList.add(ACTIVE_PIN_CLASS);
		}
		markerElement.style.setProperty("--pin-color", pinColors[properties.pinType] ?? "#374151");
		markerElement.setAttribute("aria-label", properties.title);

		const iconElement = document.createElement("span");
		iconElement.className = `icon ${pinIcons[properties.pinType]} interactieve-kaart__pin-marker-icon`;
		markerElement.appendChild(iconElement);

		markerElement.addEventListener("click", (event) => {
			event.preventDefault();
			event.stopPropagation();
			unspider();
			selectPin(toInteractieveKaartPin(properties), { centerMap: false });
		});

		markerElement.addEventListener("dblclick", (event) => {
			event.stopPropagation();
			event.preventDefault();
		});

		markerElement.addEventListener("mouseenter", () => {
			if (!isRouteType(properties.pinType)) {
				return;
			}

			hoveredRoutePinId = properties.id;
			void syncDisplayedRoute();
		});

		markerElement.addEventListener("mouseleave", () => {
			if (hoveredRoutePinId !== properties.id) {
				return;
			}

			hoveredRoutePinId = null;
			void syncDisplayedRoute();
		});

		const coordinates = feature.geometry.coordinates as [number, number];
		const marker = new mapboxgl.Marker({ element: markerElement, anchor: "bottom" })
			.setLngLat([coordinates[0], coordinates[1]])
			.addTo(map as any);

		unclusteredMarkers.push(marker);
	}
};

const createMap = (): void => {
	if (!mapElement.value) {
		return;
	}

	mapboxgl.accessToken = getMapboxAccessToken();

	const map = new mapboxgl.Map({
		container: mapElement.value,
		style: "mapbox://styles/mapbox/standard",
		center: mapCenter,
		zoom: initialZoom,
		minZoom,
		maxZoom,
		maxBounds: mapBounds,
		attributionControl: false,
	});

	mapInstance.value = map;

	map.on("load", () => {
		setupDataLayers(map);
		setupEvents(map);
		setupSpiderifier(map);

		// Render markers as soon as the clustered geojson source is ready on first load.
		map.on("sourcedata", (event) => {
			if (event.sourceId !== SOURCE_ID || !event.isSourceLoaded) {
				return;
			}

			renderUnclusteredMarkers();
		});

		map.once("idle", () => {
			renderUnclusteredMarkers();
		});

		map.on("moveend", () => renderUnclusteredMarkers());
		map.on("zoomend", () => renderUnclusteredMarkers());
	});
}

const setupDataLayers = (map: mapboxgl.Map): void => {
	map.addSource(SOURCE_ID, {
		type: "geojson",
		data: geoJsonData.value,
		cluster: true,
		clusterMaxZoom: maxZoom,
		clusterRadius: 50,
	});

	map.addSource(ROUTE_SOURCE_ID, {
		type: "geojson",
		data: emptyRouteGeoJson,
	});

	map.addLayer({
		id: ROUTE_BASE_LAYER_ID,
		type: "line",
		source: ROUTE_SOURCE_ID,
		layout: {
			"line-cap": "butt",
			"line-join": "round",
		},
		paint: {
			"line-color": ROUTE_NEUTRAL_COLOR,
			"line-width": 4,
		},
	});

	map.addLayer({
		id: ROUTE_COLOR_LAYER_ID,
		type: "line",
		source: ROUTE_SOURCE_ID,
		layout: {
			"line-cap": "butt",
			"line-join": "round",
		},
		paint: {
			"line-color": ["coalesce", ["get", "routeColor"], pinColors.wandelroute],
			"line-width": 4,
			"line-dasharray": [1, 1],
		},
	});

	map.addLayer({
		id: CLUSTER_SHADOW_LAYER_ID,
		type: "circle",
		source: SOURCE_ID,
		filter: ["has", "point_count"],
		paint: {
			"circle-color": "#000000",
			"circle-opacity": 0.6,
			"circle-blur": 1,
			"circle-radius": ["step", ["get", "point_count"], 18, 10, 22, 30, 26],
		},
	});

	map.addLayer({
		id: CLUSTER_LAYER_ID,
		type: "circle",
		source: SOURCE_ID,
		filter: ["has", "point_count"],
		paint: {
			"circle-color": "#FFFFFF",
			"circle-stroke-width": 0,
			"circle-radius": ["step", ["get", "point_count"], 18, 10, 22, 30, 26],
		},
	});

	map.addLayer({
		id: CLUSTER_COUNT_LAYER_ID,
		type: "symbol",
		source: SOURCE_ID,
		filter: ["has", "point_count"],
		layout: {
			"text-field": ["get", "point_count_abbreviated"],
			"text-size": 13,
			"text-font": ["Open Sans Semibold", "Arial Unicode MS Bold"],
		},
		paint: {
			"text-color": "#111827",
		},
	});

	map.addLayer({
		id: UNCLUSTERED_LAYER_ID,
		type: "circle",
		source: SOURCE_ID,
		filter: ["!", ["has", "point_count"]],
		paint: {
			"circle-color": "rgba(0,0,0,0)",
			"circle-radius": 1,
			"circle-stroke-width": 0,
		},
	});
}

const setupEvents = (map: mapboxgl.Map): void => {
	map.on("click", CLUSTER_LAYER_ID, (event) => {
		const clusterFeature = map.queryRenderedFeatures(event.point, { layers: [CLUSTER_LAYER_ID] })[0];
		if (!clusterFeature) {
			return;
		}

		const clusterId = Number(clusterFeature.properties?.cluster_id);
		if (!Number.isFinite(clusterId)) {
			return;
		}

		if (clusterId === activeClusterId) {
			unspider();
			return;
		}

		const isAtSpiderfyZoom = map.getZoom() >= Math.min(maxZoom, SPIDERFY_FROM_ZOOM);
		if (!isAtSpiderfyZoom) {
			unspider();
			map.easeTo({ center: event.lngLat, zoom: Math.min(map.getZoom() + 2, maxZoom) });
			return;
		}

		const clusterCoordinates: [number, number] = clusterFeature.geometry.type === "Point"
			? clusterFeature.geometry.coordinates as [number, number]
			: [event.lngLat.lng, event.lngLat.lat];

		if (clusterCoordinates.length !== 2) {
			return;
		}

		void spiderfyCluster(clusterId, clusterCoordinates);
	});

	map.on("click", (event) => {
		const clusterFeatures = map.queryRenderedFeatures(event.point, { layers: [CLUSTER_LAYER_ID] });
		if (clusterFeatures.length === 0) {
			unspider();
		}
		hoveredRoutePinId = null;
	});

	map.on("zoomstart", () => {
		unspider();
	});

	map.on("mousemove", (event) => {
		const hasFeature = map.queryRenderedFeatures(event.point, {
			layers: [CLUSTER_LAYER_ID],
		}).length > 0;
		map.getCanvas().style.cursor = hasFeature ? "pointer" : "";
	});
}

const setupSpiderifier = (map: mapboxgl.Map): void => {
	spiderifier = new MapboxglSpiderifier(map, {
		animate: true,
		animationSpeed: 180,
		customPin: true,
		circleSpiralSwitchover: 10,
		spiralLengthStart: 36,
		spiralLengthFactor: 3,
		circleFootSeparation: 40,
		initializeLeg: (leg: any) => {
			const feature = toMapFeatureProperties(leg?.feature);
			if (!feature || !leg?.elements?.pin) {
				return;
			}

			const pinElement = document.createElement("div");
			pinElement.className = "interactieve-kaart__spider-pin";
			pinElement.dataset.pinId = String(feature.id);
			if (selectedPin.value?.id === feature.id) {
				pinElement.classList.add(ACTIVE_PIN_CLASS);
			}
			pinElement.style.setProperty("--pin-color", pinColors[feature.pinType] ?? "#374151");

			const iconElement = document.createElement("span");
			iconElement.className = `icon ${pinIcons[feature.pinType]} interactieve-kaart__spider-pin-icon`;
			pinElement.appendChild(iconElement);

			leg.elements.pin.appendChild(pinElement);
			leg.elements.pin.addEventListener("mouseenter", () => {
				if (!isRouteType(feature.pinType)) {
					return;
				}

				hoveredRoutePinId = feature.id;
				void syncDisplayedRoute();
			});

			leg.elements.pin.addEventListener("mouseleave", () => {
				if (hoveredRoutePinId !== feature.id) {
					return;
				}

				hoveredRoutePinId = null;
				void syncDisplayedRoute();
			});

			leg.elements.pin.addEventListener("click", (event: MouseEvent) => {
				event.stopPropagation();
				selectPin(toInteractieveKaartPin(feature), { centerMap: false });
			});

			leg.elements.pin.addEventListener("dblclick", (event: MouseEvent) => {
				event.stopPropagation();
				event.preventDefault();
			});
		},
	});
}

const syncActivePinVisualState = (): void => {
	renderUnclusteredMarkers();

	const mapHost = mapElement.value;
	if (!mapHost) {
		return;
	}

	const spiderPins = mapHost.querySelectorAll<HTMLElement>(".interactieve-kaart__spider-pin");
	spiderPins.forEach((spiderPin) => {
		spiderPin.classList.remove(ACTIVE_PIN_CLASS);
	});

	if (!selectedPin.value) {
		return;
	}

	const activeSpiderPin = mapHost.querySelector<HTMLElement>(`.interactieve-kaart__spider-pin[data-pin-id="${selectedPin.value.id}"]`);
	activeSpiderPin?.classList.add(ACTIVE_PIN_CLASS);
}

const isRouteType = (pinType: PinType): pinType is RoutePinType => {
	return pinType === "wandelroute" || pinType === "fietsroute";
}

const setActiveRoutePinId = (pinId: number, pinType: PinType): void => {
	activeRoutePinId = isRouteType(pinType) ? pinId : null;
	void syncDisplayedRoute();
}

const syncDisplayedRoute = async (): Promise<void> => {
	const routePinId = activeRoutePinId ?? hoveredRoutePinId;
	if (!routePinId) {
		setRouteSourceData(emptyRouteGeoJson);
		return;
	}

	const routeData = await getRouteFeatureCollection(routePinId);
	setRouteSourceData(routeData ?? emptyRouteGeoJson);
}

const setRouteSourceData = (data: GeoJSON.FeatureCollection<GeoJSON.LineString, RouteFeatureProperties>): void => {
	const map = mapInstance.value;
	if (!map) {
		return;
	}

	const routeSource = map.getSource(ROUTE_SOURCE_ID) as mapboxgl.GeoJSONSource | undefined;
	if (!routeSource) {
		return;
	}

	routeSource.setData(data);
}

const getRouteFeatureCollection = async (pinId: number): Promise<GeoJSON.FeatureCollection<GeoJSON.LineString, RouteFeatureProperties> | null> => {
	const cached = routeDataCache.get(pinId);
	if (cached) {
		return cached;
	}

	const routePin = filteredPins.value.find((pin) => pin.id === pinId);
	if (!routePin || !isRouteType(routePin.type)) {
		return null;
	}

	let gpxContent = routePin.gpxXml?.trim() ?? "";
	if (!gpxContent && routePin.gpxUrl) {
		try {
			const response = await fetch(routePin.gpxUrl);
			if (!response.ok) {
				return null;
			}
			gpxContent = await response.text();
		} catch {
			return null;
		}
	}

	if (!gpxContent) {
		return null;
	}

	const parsedRoute = parseGpxToFeatureCollection(pinId, routePin.type, gpxContent);
	if (!parsedRoute || parsedRoute.features.length === 0) {
		return null;
	}

	routeDataCache.set(pinId, parsedRoute);
	return parsedRoute;
}

const parseGpxToFeatureCollection = (
	pinId: number,
	routeType: RoutePinType,
	gpxContent: string,
): GeoJSON.FeatureCollection<GeoJSON.LineString, RouteFeatureProperties> | null => {
	const parsedXml = new DOMParser().parseFromString(gpxContent, "application/xml");
	if (parsedXml.querySelector("parsererror")) {
		return null;
	}

	const routeColor = pinColors[routeType];
	const lineFeatures: GeoJSON.Feature<GeoJSON.LineString, RouteFeatureProperties>[] = [];

	const trackSegments = parsedXml.querySelectorAll("trkseg");
	trackSegments.forEach((segment) => {
		const coordinates = parsePointNodes(segment.querySelectorAll("trkpt"));
		if (coordinates.length < 2) {
			return;
		}

		lineFeatures.push({
			type: "Feature",
			geometry: {
				type: "LineString",
				coordinates,
			},
			properties: {
				pinId,
				routeColor,
			},
		});
	});

	if (lineFeatures.length === 0) {
		const routeNodes = parsedXml.querySelectorAll("rte");
		routeNodes.forEach((route) => {
			const coordinates = parsePointNodes(route.querySelectorAll("rtept"));
			if (coordinates.length < 2) {
				return;
			}

			lineFeatures.push({
				type: "Feature",
				geometry: {
					type: "LineString",
					coordinates,
				},
				properties: {
					pinId,
					routeColor,
				},
			});
		});
	}

	if (lineFeatures.length === 0) {
		return null;
	}

	return {
		type: "FeatureCollection",
		features: lineFeatures,
	};
}

const parsePointNodes = (pointNodes: NodeListOf<Element>): [number, number][] => {
	const coordinates: [number, number][] = [];

	pointNodes.forEach((pointNode) => {
		const latitude = Number(pointNode.getAttribute("lat"));
		const longitude = Number(pointNode.getAttribute("lon"));

		if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
			return;
		}

		coordinates.push([longitude, latitude]);
	});

	return coordinates;
}

const toMapFeatureProperties = (properties: unknown): MapFeatureProperties | null => {
	if (!properties || typeof properties !== "object") {
		return null;
	}

	const mapProperties = properties as Record<string, unknown>;
	const pinType = String(mapProperties.pinType ?? "") as PinType;

	const id = Number(mapProperties.id);
	const latitude = Number(mapProperties.latitude);
	const longitude = Number(mapProperties.longitude);

	if (!Number.isFinite(id) || !Number.isFinite(latitude) || !Number.isFinite(longitude)) {
		return null;
	}

	return {
		id,
		title: String(mapProperties.title ?? ""),
		pinType,
		latitude,
		longitude,
	};
}

const unspider = (): void => {
	if (spiderifier) {
		spiderifier.unspiderfy();
	}
	activeClusterId = null;
}

watch(selectedPin, () => {
	syncActivePinVisualState();
});

watch(filteredPins, (newPins, oldPins) => {
	const map = mapInstance.value;
	const source = map?.getSource(SOURCE_ID) as mapboxgl.GeoJSONSource | undefined;
	if (source) {
		source.setData(geoJsonData.value);
	}

	if (selectedPin.value && !newPins.some((pin) => pin.id === selectedPin.value?.id)) {
		clearSelectedPin();
	}

	const newIds = newPins.map((p) => p.id).join();
	const oldIds = (oldPins ?? []).map((p) => p.id).join();
	if (newIds !== oldIds) {
		unspider();
	}

	renderUnclusteredMarkers();
}, { deep: true });

const onSidebarActivityChange = (activity: ActivityType): void => {
	selectedActivity.value = activity;
};

const onSidebarActivityFiltersChange = (filters: ActivitySidebarFilters): void => {
	activityFilters.value = filters;
	if (selectedActivity.value !== "spelen") {
		void loadActivityPins(filters);
	}
};

const onSidebarClubFiltersChange = (filters: Record<string, string[]>): void => {
	clubFilters.value = filters;
	if (selectedActivity.value === "spelen") {
		void loadClubPins(filters);
	}
};


const onSidebarPlayTypesChange = (playTypes: string[]): void => {
	selectedPlayTypes.value = playTypes;
};

watch(selectedActivity, async (newValue) => {
	clearSelectedPin();
	selectedPlayTypes.value = [];
	activityFilters.value = {};
	clubFilters.value = {};
	clubPins.value = [];
	activityPins.value = [];
	activityFacetGroups.value = [];
	clubFacetGroups.value = [];
	loadedPinDetailKeys.clear();
	loadingPinDetailKeys.clear();

	if (newValue === "spelen") {
		loadPlayPins();
		return;
	}

	await loadActivityPins(activityFilters.value);
});

onMounted(() => {
	previousBodyOverflow = document.body.style.overflow;
	document.body.style.overflow = "hidden";
	void platformStore.ensurePlatform().then(() => {
		createMap();
		if (selectedActivity.value === "spelen") {
			loadPlayPins();
			return;
		}

		void loadActivityPins(activityFilters.value);
	});
});

onUnmounted(() => {
	document.body.style.overflow = previousBodyOverflow;
	unspider();
	routeDataCache.clear();
	loadedPinDetailKeys.clear();
	loadingPinDetailKeys.clear();
	clearUnclusteredMarkers();
	if (userLocationMarker) {
		userLocationMarker.remove();
		userLocationMarker = null;
	}
	if (mapInstance.value) {
		mapInstance.value.remove();
		mapInstance.value = null;
	}
});
</script>

<template>
	<Notifications position="top right" classes="custom-notifications" animation-type="velocity" aria-live="polite"
        aria-atomic="true" />
	<section class="interactieve-kaart">
		<div ref="mapElement" class="interactieve-kaart__map"></div>

		<InteractieveKaartSidebar :active="selectedActivity" :pins="pins" :result-count="filteredPins.length"
			:activity-facet-groups="activityFacetGroups" :club-facet-groups="clubFacetGroups"
			@change-activity="onSidebarActivityChange" @change-play-types="onSidebarPlayTypesChange"
			@change-activity-filters="onSidebarActivityFiltersChange"
			@change-club-filters="onSidebarClubFiltersChange" />

		<InteractieveKaartActions @locate-user="onLocateUser" @zoom-in="onZoomIn" @zoom-out="onZoomOut" />

		<InteractieveKaartOverlay :pins="overlayPins" :selected-pin-id="selectedPin?.id ?? null" :pin-colors="pinColors"
			:pin-icons="pinIcons" @select-pin="onOverlaySelectPin" @focus-pin="onOverlayFocusPin"
			@hover-pin="onOverlayHoverPin" />
	</section>
</template>